import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, ShieldCheck, Headphones, Building2 } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Card } from '../../components/ui/Card';
import { Reveal } from '../../components/landing/Reveal';
import { FormError, SubmitButton, TextField } from '../../components/auth/FormFields';

/**
 * Contact page. Two sections:
 *   1. Direct-route mailboxes — fastest for known intents.
 *   2. A general-purpose form for everything else. The form posts to a
 *      lightweight /api/contact endpoint (added separately) that mails
 *      the platform's support inbox via the existing notification module.
 */
export function ContactPage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, body }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSent(true);
    } catch {
      setErr(t('contact.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  const mailboxes = [
    { icon: Headphones, key: 'support', addr: 'support@plyez.com' },
    { icon: ShieldCheck, key: 'security', addr: 'security@plyez.com' },
    { icon: Building2, key: 'enterprise', addr: 'enterprise@plyez.com' },
    { icon: Mail, key: 'press', addr: 'press@plyez.com' },
  ] as const;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-12 lg:py-16">
        <Reveal as="div">
          <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
            <span className="sparkle-dot" aria-hidden />
            {t('contact.eyebrow')}
          </span>
          <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-4">
            {t('contact.title')}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">{t('contact.subtitle')}</p>
        </Reveal>

        <div className="rule mt-10" aria-hidden />

        <div className="mt-12 grid lg:grid-cols-[1fr_1fr] gap-10">
          <Reveal as="div" className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('contact.directRoutes')}
            </div>
            {mailboxes.map((m) => {
              const Icon = m.icon;
              return (
                <Card key={m.key} className="rounded-2xl border-border p-4 flex items-start gap-3 transition-shadow hover:shadow-soft">
                  <div className="h-9 w-9 rounded-xl bg-accent-50 text-accent-700 dark:bg-accent-900 dark:text-accent-100 flex items-center justify-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium">{t(`contact.mailboxes.${m.key}.title`)}</div>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                      {t(`contact.mailboxes.${m.key}.body`)}
                    </p>
                    <a
                      href={`mailto:${m.addr}`}
                      className="num mt-2 inline-block text-sm text-accent-700 dark:text-accent-300 underline underline-offset-4 rounded-sm"
                    >
                      {m.addr}
                    </a>
                  </div>
                </Card>
              );
            })}
          </Reveal>

          <Reveal as="div" delay={0.06}>
          <Card className="rounded-2xl border-border p-6">
            <div className="display text-lg font-semibold mb-1">{t('contact.formTitle')}</div>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{t('contact.formSubtitle')}</p>
            {sent ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 text-sm px-4 py-3">
                {t('contact.sentBody')}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <TextField
                  label={t('auth.fullName')}
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextField
                  label={t('common.email')}
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  label={t('contact.subjectLabel')}
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <label className="block">
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1.5">
                    {t('contact.messageLabel')}
                  </div>
                  <textarea
                    rows={6}
                    required
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
                  />
                </label>
                {err && <FormError>{err}</FormError>}
                <SubmitButton loading={busy}>{t('contact.send')}</SubmitButton>
              </form>
            )}
          </Card>
          </Reveal>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
